import { useMemo } from 'react';
import { Box, Paper, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingsNav, type SettingsNavGroup } from '../components';
import { SETTINGS_NAV_GROUPS } from './settingsNav';

/**
 * The Settings two-pane shell (prototype `SettingsPane` / `SettingsRouter`).
 * Left: the sticky grouped {@link SettingsNav} (Catalog / Budgets / Notifications
 * / Data) driven by {@link SETTINGS_NAV_GROUPS}. Right: the active sub-page via
 * `<Outlet/>`. Each sub-page owns its `<PageHeader/>`, so the title/subtitle
 * still render in the TopBar. On narrow viewports the rail stacks above the
 * content so the section is still reachable.
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const isNarrow = useMediaQuery('(max-width:899.95px)');

  const groups: SettingsNavGroup[] = useMemo(
    () =>
      SETTINGS_NAV_GROUPS.map((group) => ({
        key: group.key,
        title: t(group.titleKey),
        items: group.items.map((item) => ({
          key: item.key,
          label: t(item.labelKey),
          to: item.to,
          icon: item.icon,
          badge: item.badge,
        })),
      })),
    [t],
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isNarrow ? '1fr' : '236px minmax(0, 1fr)',
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: '8px',
          position: isNarrow ? 'static' : 'sticky',
          top: 24,
        }}
      >
        <SettingsNav groups={groups} />
      </Paper>
      <Box sx={{ minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
